import type { ProxyFetchFailedPayload } from '../lib/connector';
import { useMaestroStore } from '../store/maestroStore';
import { showImperativeToast } from '../providers/UIProvider';
import { diagnosticLog } from './diagnosticLogger';
import { getTauriDesktopOs } from './platform';
import { isDeepBufferTimerRenderer } from '../transport/types'; import { rollbackOptimisticPlayingIfNeeded } from './proxyFetchFailedPlayingRollback';

const TOAST_MS = 14_000;
/** Same play attempt — proxy fetch check fires ~10s after dispatch. */
const ATTEMPT_WINDOW_MS = 120_000;
/** Suppress reachability/isolation UI while a proxy stream is in flight or just finished. */
const DELIVERY_EVIDENCE_MS = 60_000;
/** Transport-truth window for pairing SOAP/STOPPED failures with proxy telemetry. */
const TRANSPORT_FAILURE_WINDOW_MS = 120_000;

export type IsolationFailureBanner = {
  visible: boolean;
  rendererName: string;
  message: string;
};

type TrackedFailure = { at: number; payload: ProxyFetchFailedPayload };

const shownThisSession = new Set<string>();
const recentFailures = new Map<string, TrackedFailure>();
const attemptStartedAt = new Map<string, number>();
const playSucceededAt = new Map<string, number>();
const transportPlayFailedAt = new Map<string, number>();
const streamInFlightAt = new Map<string, number>();
const streamCompletedAt = new Map<string, number>();

let isolationBanner: IsolationFailureBanner | null = null;
const isolationBannerListeners = new Set<() => void>();

function notifyIsolationBannerListeners(): void {
  for (const fn of isolationBannerListeners) {
    try { fn(); } catch { /* ignore */ }
  }
}

export function getIsolationFailureBanner(): IsolationFailureBanner | null {
  return isolationBanner;
}

export function subscribeIsolationFailureBanner(listener: () => void): () => void {
  isolationBannerListeners.add(listener);
  return () => { isolationBannerListeners.delete(listener); };
}

export function dismissIsolationFailureBanner(): void {
  if (!isolationBanner?.visible) return;
  isolationBanner = { ...isolationBanner, visible: false };
  notifyIsolationBannerListeners();
}

function hideIsolationBannerIfVisible(): void {
  if (!isolationBanner?.visible) return;
  isolationBanner = { ...isolationBanner, visible: false };
  notifyIsolationBannerListeners();
}

function stampKeys(map: Map<string, number>, keys: string[], at: number): void {
  for (const key of keys) map.set(key, at);
}

function deliveryKeys(
  rendererIp?: string | null,
  rendererName?: string | null,
  rendererId?: string | null,
): string[] {
  return [rendererIp, rendererName, rendererId].map((k) => String(k ?? '').trim()).filter(Boolean);
}

/** Renderer started a proxy fetch — stream is in flight. */
export function markProxyStreamRequest(
  rendererIp?: string | null,
  rendererName?: string | null,
  rendererId?: string | null,
): void {
  const keys = deliveryKeys(rendererIp, rendererName, rendererId);
  if (keys.length === 0) return;
  const now = Date.now();
  stampKeys(streamInFlightAt, keys, now);
  hideIsolationBannerIfVisible();
}

/** Renderer finished a proxy fetch. Delivery evidence stays valid for 60s. */
export function markProxyStreamComplete(
  rendererIp?: string | null,
  rendererName?: string | null,
  rendererId?: string | null,
): void {
  const keys = deliveryKeys(rendererIp, rendererName, rendererId);
  if (keys.length === 0) return;
  const now = Date.now();
  for (const key of keys) streamInFlightAt.delete(key);
  stampKeys(streamCompletedAt, keys, now);
  hideIsolationBannerIfVisible();
}

function showIsolationFailureBanner(payload: ProxyFetchFailedPayload, message: string): void {
  isolationBanner = {
    visible: true,
    rendererName: payload.renderer_name || 'Your renderer',
    message,
  };
  notifyIsolationBannerListeners();
}

function normalizePlatform(raw: unknown): 'macos' | 'windows' | 'linux' | 'other' {
  const p = String(raw ?? '').trim().toLowerCase();
  if (p === 'macos' || p === 'darwin' || p === 'mac') return 'macos';
  if (p === 'windows' || p === 'win32' || p === 'win') return 'windows';
  if (p === 'linux') return 'linux';
  return getTauriDesktopOs();
}

function resolveActiveRendererContext(): {
  renderer: { id: string; name?: string } | null;
  zone: ReturnType<typeof useMaestroStore.getState>['discovery']['activeZone'];
} {
  const store = useMaestroStore.getState();
  const renderer = store.activeConnectorRenderer;
  const zone =
    store.discovery?.activeZone?.id === renderer?.id
      ? store.discovery.activeZone
      : store.discovery?.discoveredZones?.find((z) => z.id === renderer?.id) ?? store.discovery?.activeZone ?? null;
  return { renderer, zone };
}

function isDeepBufferActiveRenderer(
  rendererId?: string | null,
  rendererName?: string | null,
): boolean {
  const { renderer, zone } = resolveActiveRendererContext();
  if (!renderer) return false;
  if (rendererId && renderer.id !== rendererId) return false;
  if (rendererName && renderer.name && rendererName !== renderer.name) return false;
  return isDeepBufferTimerRenderer(renderer, zone);
}

/** User-facing guidance when a network renderer never fetched the proxy stream. */
export function buildProxyFetchFailedGuidance(
  rendererName: string,
  platform?: 'macos' | 'windows' | 'linux' | 'other',
  selfProbe?: 'ok' | 'fail',
): string {
  const name = (rendererName || 'Your renderer').trim();

  if (selfProbe === 'ok') {
    return `${name} can't reach this computer over your network, even though this computer is healthy. This is usually router 'client/AP isolation' or a guest-network setting — check your router's Wi-Fi settings, or try a wired connection.`;
  }

  const os = platform ?? getTauriDesktopOs();
  const lead = `${name} accepted the connection but can't download audio from this computer — usually a firewall.`;
  if (os === 'macos') {
    return `${lead} macOS: System Settings → Network/Firewall → allow Maestro.`;
  }
  if (os === 'windows') {
    return `${lead} Windows: allow Maestro through Windows Defender Firewall.`;
  }
  return `${lead} Check that this computer allows incoming connections from devices on your network.`;
}

function sessionKey(payload: ProxyFetchFailedPayload): string {
  return payload.renderer_ip || payload.renderer_name || 'unknown';
}

function recordFailure(payload: ProxyFetchFailedPayload): void {
  const entry: TrackedFailure = { at: Date.now(), payload };
  recentFailures.set(sessionKey(payload), entry);
  if (payload.renderer_ip) recentFailures.set(payload.renderer_ip, entry);
  if (payload.renderer_name) recentFailures.set(payload.renderer_name, entry);

  const store = useMaestroStore.getState();
  const rid = store.activeConnectorRenderer?.id || store.discovery?.activeZone?.id;
  if (rid) recentFailures.set(rid, entry);
}

/** SOAP Play failure or renderer STOPPED — not proxy-fetch timing alone. */
export function markTransportPlayFailure(rendererId?: string | null): void {
  if (!rendererId) return;
  transportPlayFailedAt.set(rendererId, Date.now());
}

export function hasRecentTransportPlayFailure(rendererId?: string | null): boolean {
  if (!rendererId) return false;
  const at = transportPlayFailedAt.get(rendererId);
  if (at == null) return false;
  return Date.now() - at < TRANSPORT_FAILURE_WINDOW_MS;
}

/** Call when a network-renderer play dispatch succeeds (starts attempt window). */
export function markNetworkPlayAttemptStarted(rendererId?: string | null): void {
  if (!rendererId) return;
  attemptStartedAt.set(rendererId, Date.now());
}

/** Call when a network renderer transitions to confirmed remote playback. */
export function markNetworkPlaySucceeded(
  rendererIp?: string | null,
  rendererName?: string | null,
): void {
  const now = Date.now();
  const ip = String(rendererIp ?? '').trim();
  const name = String(rendererName ?? '').trim();
  if (ip) playSucceededAt.set(ip, now);
  if (name) playSucceededAt.set(name, now);
}

function payloadIdentityKeys(payload: ProxyFetchFailedPayload): string[] {
  return [
    sessionKey(payload),
    payload.renderer_ip,
    payload.renderer_name,
    payload.fetch_from,
  ].filter(Boolean) as string[];
}

type DeliveryEvidence = { inFlight: boolean; ageMs: number };

function payloadEmbeddedDelivery(payload: ProxyFetchFailedPayload): DeliveryEvidence | null {
  const ageHint = typeof payload.last_delivery_age_ms === 'number'
    ? payload.last_delivery_age_ms
    : null;
  if (payload.stream_in_flight === true) {
    return { inFlight: true, ageMs: ageHint ?? 0 };
  }
  if (payload.device_reached_us === true) {
    const age = ageHint ?? 0;
    return { inFlight: age === 0, ageMs: age };
  }
  if (typeof payload.bytes_delivered === 'number' && payload.bytes_delivered > 0) {
    const age = ageHint ?? 0;
    if (age <= DELIVERY_EVIDENCE_MS) return { inFlight: false, ageMs: age };
  }
  if (ageHint != null && ageHint >= 0 && ageHint <= DELIVERY_EVIDENCE_MS) {
    return { inFlight: false, ageMs: ageHint };
  }
  return null;
}

function transportDeliveryEvidence(keys: string[]): DeliveryEvidence | null {
  const store = useMaestroStore.getState();
  const state = String(store.playbackState || '');
  if (state !== 'playing' && state !== 'buffering' && state !== 'loading') return null;
  const renderer = store.activeConnectorRenderer;
  const zone = store.discovery?.activeZone;
  const transportKeys = [
    renderer?.id,
    renderer?.name,
    zone?.id,
    zone?.name,
    zone?.host,
    (zone as { ip?: string } | null | undefined)?.ip,
  ].filter(Boolean) as string[];
  if (transportKeys.length === 0) return null;
  const matches = keys.some((k) => transportKeys.includes(k));
  if (!matches) return null;
  return { inFlight: true, ageMs: 0 };
}

function trackedDeliveryEvidence(keys: string[]): DeliveryEvidence | null {
  const now = Date.now();
  let oldestInFlight: number | null = null;
  let newestComplete: number | null = null;

  for (const key of keys) {
    const started = streamInFlightAt.get(key);
    if (started != null && (oldestInFlight == null || started < oldestInFlight)) {
      oldestInFlight = started;
    }
    const completed = streamCompletedAt.get(key);
    const succeeded = playSucceededAt.get(key);
    const at = Math.max(completed ?? 0, succeeded ?? 0);
    if (at > 0 && (newestComplete == null || at > newestComplete)) newestComplete = at;
  }

  if (oldestInFlight != null) {
    return { inFlight: true, ageMs: Math.max(0, now - oldestInFlight) };
  }
  if (newestComplete != null && now - newestComplete <= DELIVERY_EVIDENCE_MS) {
    return { inFlight: false, ageMs: now - newestComplete };
  }
  return null;
}

function findDeliveryEvidence(payload: ProxyFetchFailedPayload): DeliveryEvidence | null {
  return payloadEmbeddedDelivery(payload)
    || trackedDeliveryEvidence(payloadIdentityKeys(payload))
    || transportDeliveryEvidence(payloadIdentityKeys(payload));
}

function emitDeliverySuppressBeacon(
  payload: ProxyFetchFailedPayload,
  evidence: DeliveryEvidence,
): void {
  diagnosticLog('info', 'playback', 'isolation_banner_suppressed_delivery_evidence', {
    renderer: payload.renderer_name || payload.renderer_ip || 'unknown',
    last_delivery_age_ms: evidence.ageMs,
  });
}

function isRetryEngineRejectionMessage(errMsg: string): boolean {
  const lower = errMsg.toLowerCase();
  return (
    lower.includes('rejected the audio stream')
    || lower.includes('rejected the stream')
    || (lower.includes('setavtransporturi') && lower.includes('attempt'))
  );
}

function isSoapOrStoppedTransportFailure(errMsg: string): boolean {
  const lower = errMsg.toLowerCase();
  if (lower.includes('play failed') && (lower.includes('soap') || lower.includes('http 500'))) {
    return true;
  }
  if (lower.includes('setavtransporturi ok') && lower.includes('play')) {
    return true;
  }
  if (lower.includes('transport') && lower.includes('stopped')) {
    return true;
  }
  if (lower.includes('currenttransportstate') && lower.includes('stopped')) {
    return true;
  }
  return false;
}

function findProxyFetchFailureInAttemptWindow(
  rendererId?: string | null,
  rendererName?: string | null,
): ProxyFetchFailedPayload | null {
  const now = Date.now();
  const windowStart = rendererId && attemptStartedAt.has(rendererId)
    ? attemptStartedAt.get(rendererId)!
    : now - ATTEMPT_WINDOW_MS;

  const keys = [
    rendererId,
    rendererName,
    useMaestroStore.getState().discovery?.activeZone?.ip,
    useMaestroStore.getState().activeConnectorRenderer?.name,
  ].filter(Boolean) as string[];

  for (const key of keys) {
    const hit = recentFailures.get(key);
    if (hit && hit.at >= windowStart) return hit.payload;
  }

  let newest: TrackedFailure | null = null;
  for (const hit of recentFailures.values()) {
    if (hit.at < windowStart) continue;
    if (!newest || hit.at > newest.at) newest = hit;
  }
  return newest?.payload ?? null;
}

/** True when a recent proxy_fetch_failed had self_probe=ok (AP/client isolation). */
export function hasRecentIsolationFailure(
  rendererId?: string | null,
  rendererName?: string | null,
): boolean {
  const match = findProxyFetchFailureInAttemptWindow(rendererId, rendererName);
  return match?.self_probe === 'ok';
}

function logProxyFetchTelemetry(
  payload: ProxyFetchFailedPayload,
  reason: string,
): void {
  diagnosticLog('warn', 'playback', 'proxy_fetch_failed telemetry', {
    reason,
    renderer_name: payload.renderer_name,
    renderer_ip: payload.renderer_ip,
    stream_host: payload.stream_host,
    stream_port: payload.stream_port,
    platform: payload.platform,
    self_probe: payload.self_probe ?? null,
    device_never_reached_us: true,
  });
}

/** Connector `proxy_fetch_failed` — toast once per renderer per session (unless deep-buffer class). */
export function handleProxyFetchFailedEvent(payload: ProxyFetchFailedPayload): void {
  recordFailure(payload);
  rollbackOptimisticPlayingIfNeeded(payload);
  const delivery = findDeliveryEvidence(payload);
  if (delivery) {
    emitDeliverySuppressBeacon(payload, delivery);
    if (payload.self_probe === 'fail') {
      logProxyFetchTelemetry(payload, 'self_probe_fail_with_delivery');
    } else {
      console.log(
        `[proxyFetchFailedUI] suppressed: delivery evidence for ${payload.renderer_name} age_ms=${delivery.ageMs}`,
      );
    }
    return;
  }

  if (payload.self_probe === 'fail') {
    logProxyFetchTelemetry(payload, 'self_probe_fail');
    console.log(
      `[proxyFetchFailedUI] self-probe fail is telemetry-only for ${payload.renderer_name}`,
    );
  }

  const store = useMaestroStore.getState();
  const rendererId = store.activeConnectorRenderer?.id || store.discovery?.activeZone?.id;
  const deepBuffer = isDeepBufferActiveRenderer(rendererId, payload.renderer_name);

  if (deepBuffer) {
    logProxyFetchTelemetry(payload, 'deep_buffer_timer_renderer');
    console.log(
      `[proxyFetchFailedUI] telemetry-only for deep-buffer renderer ${payload.renderer_name} — no user toast`,
    );
    return;
  }

  const key = sessionKey(payload);

  if (payload.self_probe === 'ok') {
    const playAttempted = !rendererId || attemptStartedAt.has(rendererId);
    if (!playAttempted) {
      logProxyFetchTelemetry(payload, 'isolation_without_play_attempt');
      return;
    }
    const message = buildProxyFetchFailedGuidance(
      payload.renderer_name,
      normalizePlatform(payload.platform),
      payload.self_probe,
    );
    if (!shownThisSession.has(key)) {
      shownThisSession.add(key);
      showIsolationFailureBanner(payload, message);
      diagnosticLog('warn', 'playback', 'proxy_fetch_failed isolation banner shown', {
        renderer_name: payload.renderer_name,
        renderer_ip: payload.renderer_ip,
        self_probe: payload.self_probe,
        guidance: message,
      });
    }
    return;
  }

  if (!hasRecentTransportPlayFailure(rendererId)) {
    logProxyFetchTelemetry(payload, 'no_confirmed_transport_failure');
    console.log(
      `[proxyFetchFailedUI] telemetry-only — no SOAP/STOPPED transport failure for ${payload.renderer_name}`,
    );
    return;
  }

  if (shownThisSession.has(key)) return;
  shownThisSession.add(key);

  const message = buildProxyFetchFailedGuidance(
    payload.renderer_name,
    normalizePlatform(payload.platform),
    payload.self_probe,
  );
  showImperativeToast(message, TOAST_MS);
  diagnosticLog('warn', 'playback', 'proxy_fetch_failed guidance shown', {
    renderer_name: payload.renderer_name,
    renderer_ip: payload.renderer_ip,
    stream_host: payload.stream_host,
    stream_port: payload.stream_port,
    platform: payload.platform,
    self_probe: payload.self_probe ?? null,
    guidance: message,
  });
}

/**
 * When the retry engine reports a stream rejection but the renderer never fetched
 * from the proxy, replace misleading "rejected the stream" copy with firewall guidance.
 */
export function rewritePlayErrorIfProxyFetchBlocked(
  errMsg: string,
  rendererId?: string | null,
  rendererName?: string | null,
): string {
  if (!errMsg || !isRetryEngineRejectionMessage(errMsg)) return errMsg;

  const match = findProxyFetchFailureInAttemptWindow(rendererId, rendererName);
  if (!match) return errMsg;

  if (isDeepBufferActiveRenderer(rendererId, rendererName)) {
    return errMsg;
  }

  const transportConfirmed =
    hasRecentTransportPlayFailure(rendererId)
    || isSoapOrStoppedTransportFailure(errMsg);
  if (!transportConfirmed) return errMsg;

  const name = rendererName || match.renderer_name || 'Your renderer';
  const rewritten = buildProxyFetchFailedGuidance(
    name,
    normalizePlatform(match.platform),
    match.self_probe,
  );
  diagnosticLog('warn', 'playback', 'proxy_fetch_failed error rewrite', {
    original_error_truncated: errMsg.slice(0, 240),
    renderer_id: rendererId ?? null,
    renderer_name: name,
    renderer_ip: match.renderer_ip,
    self_probe: match.self_probe ?? null,
    guidance: rewritten,
  });
  return rewritten;
}

/** @deprecated Import from playFailureUI — kept for existing call sites. */
export { showNetworkPlayFailureToast } from './playFailureUI';

/** Test-only: clear session maps and hide any isolation banner. */
export function resetProxyFetchFailedUIForTests(): void {
  shownThisSession.clear();
  recentFailures.clear();
  attemptStartedAt.clear();
  playSucceededAt.clear();
  transportPlayFailedAt.clear();
  streamInFlightAt.clear();
  streamCompletedAt.clear();
  isolationBanner = null;
  notifyIsolationBannerListeners();
}
